import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { getAuditLogs } from '../../../services/adminService';

const AuditLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    table_name: '',
    action: '',
    start_date: '',
    end_date: ''
  });
  const [error, setError] = useState('');

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const data = await getAuditLogs(filters);
      setLogs(data || []);
    } catch (error) {
      setError(`Failed to load audit logs: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuditLogs();
  }, [filters]);

  const tableOptions = [
    { value: '', label: 'All Tables' },
    { value: 'user_profiles', label: 'User Profiles' },
    { value: 'events', label: 'Events' },
    { value: 'venues', label: 'Venues' },
    { value: 'orders', label: 'Orders' },
    { value: 'system_configuration', label: 'System Configuration' }
  ];

  const actionOptions = [
    { value: '', label: 'All Actions' },
    { value: 'INSERT', label: 'Created' },
    { value: 'UPDATE', label: 'Updated' },
    { value: 'DELETE', label: 'Deleted' }
  ];

  const getActionColor = (action) => {
    const colors = {
      INSERT: 'bg-green-100 text-green-800',
      UPDATE: 'bg-blue-100 text-blue-800',
      DELETE: 'bg-red-100 text-red-800'
    };
    return colors?.[action] || 'bg-gray-100 text-gray-800';
  };

  const formatJsonDiff = (oldValues, newValues) => {
    if (!oldValues && !newValues) return 'No changes';
    
    const changes = [];
    
    if (oldValues && newValues) {
      // Show what changed
      Object.keys(newValues)?.forEach(key => {
        if (oldValues?.[key] !== newValues?.[key]) {
          changes?.push(`${key}: ${oldValues?.[key]} → ${newValues?.[key]}`);
        }
      });
    } else if (newValues) {
      // New record
      changes?.push('New record created');
    } else if (oldValues) {
      // Deleted record
      changes?.push('Record deleted');
    }
    
    return changes?.join(', ') || 'No changes detected';
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="FileText" size={20} />
          Audit Log
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Track all administrative changes and system modifications
        </p>
      </div>
      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {/* Filters */}
      <div className="p-6 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Select
            label="Table"
            value={filters?.table_name}
            onChange={(e) => setFilters({...filters, table_name: e?.target?.value})}
            options={tableOptions}
          />
          <Select
            label="Action"
            value={filters?.action}
            onChange={(e) => setFilters({...filters, action: e?.target?.value})}
            options={actionOptions}
          />
          <Input
            label="Start Date"
            type="date"
            value={filters?.start_date}
            onChange={(e) => setFilters({...filters, start_date: e?.target?.value})}
          />
          <Input
            label="End Date"
            type="date"
            value={filters?.end_date}
            onChange={(e) => setFilters({...filters, end_date: e?.target?.value})}
          />
        </div>
      </div>
      {/* Audit Logs */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">User</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Action</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Table</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Changes</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-8 text-muted-foreground">
                  Loading audit logs...
                </td>
              </tr>
            ) : logs?.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-8 text-muted-foreground">
                  No audit logs found
                </td>
              </tr>
            ) : (
              logs?.map((log) => (
                <tr key={log?.id} className="border-t border-border hover:bg-muted/50">
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-foreground">
                        {log?.user?.full_name || 'System'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {log?.user?.email || 'system@tixful.com'}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log?.action)}`}>
                      {log?.action}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-foreground">{log?.table_name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-muted-foreground max-w-md truncate" title={formatJsonDiff(log?.old_values, log?.new_values)}>
                      {formatJsonDiff(log?.old_values, log?.new_values)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {new Date(log?.created_at)?.toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditLogViewer;